// main.ts — Platik-IA
// Punto de entrada: registra módulos → boot() → monta UI.

import '/src/design/base.css';

import { initTheme } from './core/theme';
import { register, boot, getModule } from './core/registry';

// Aplicar tema (claro/oscuro/sistema) antes de renderizar contenido temático.
initTheme();
import { bus }     from './core/event-bus';
import { mount, removeBoot } from './core/mounter';

// ── Servicios compartidos ───────────────────────────────────────────────
import { AppModal }         from './shared/components/modal';
import { AppEmptyState }    from './shared/components/empty-state';

// ── Módulos: init ───────────────────────────────────────────────────────
import { initNotifications } from './modules/notifications';
import { initAuth }          from './modules/auth';
import { initCategories }    from './modules/categories';
import { initSync }          from './modules/sync';
import { initTransactions }  from './modules/transactions';
import { initVoice }         from './modules/voice';
import { initAnalytics }     from './modules/analytics';
import { initNavigation }    from './modules/navigation';
import { initSettings }      from './modules/settings';
import { initImport }        from './modules/import';
import { initAdmin }         from './modules/admin';

// ── Módulos: UI ─────────────────────────────────────────────────────────
import { AppAuth }              from './modules/auth/ui';
import { AppNotifications }     from './modules/notifications/ui';
import { AppNavigation }        from './modules/navigation/ui';
import { AppPanelRegistro, AppPanelHistorial } from './modules/transactions/ui';
import { AppPanelAnalisis }     from './modules/analytics/ui';
import { AppPanelCategorias }   from './modules/categories/ui';
import { AppPanelConfig }       from './modules/settings/ui';
import { AppAdmin }             from './modules/admin/ui';

// ── Registrar Web Components ────────────────────────────────────────────
AppModal.define();
AppEmptyState.define();
AppAuth.define();
AppNotifications.define();
AppNavigation.define();
AppPanelRegistro.define();
AppPanelHistorial.define();
AppPanelAnalisis.define();
AppPanelCategorias.define();
AppPanelConfig.define();
AppAdmin.define();

// ── Registrar módulos (orden determinado por DAG) ───────────────────────
register('notifications', [],                         initNotifications);
register('auth',          ['notifications'],          initAuth);
register('categories',    ['auth'],                   initCategories);
register('sync',          ['auth'],                   initSync);
register('transactions',  ['auth', 'sync'],           initTransactions);
register('voice',         ['auth', 'categories'],     initVoice);
register('analytics',     ['transactions'],           initAnalytics);
register('navigation',    ['auth'],                   initNavigation);
register('settings',      ['auth'],                   initSettings);
register('import',        ['auth', 'sync'],           initImport);
register('admin',         ['auth'],                   initAdmin);

// ── LATER (descomentar cuando estén listos) ─────────────────────────────
// register('payments', ['auth'], initPayments);

// ── Montar UI tras boot ─────────────────────────────────────────────────
bus.on('app:ready', () => {
  removeBoot();
  const root = document.getElementById('app-root')!;

  // Toasts globales
  mount('#app-root', 'app-notifications');

  // ¿Usuario autenticado?
  const auth = getModule<{ getUser: () => { role: string } | null; hasSession: () => boolean }>('auth');
  const user = auth?.getUser();

  if (user) {
    // Perfil ya cargado (raro en este punto, pero posible) → shell principal.
    mountShell(user.role);
  } else if (auth?.hasSession()) {
    // Hay sesión persistida pero el perfil aún se está cargando (async).
    // Mostramos un spinner de carga, NO la pantalla de login (evita el flash).
    const loader = document.createElement('app-auth');
    loader.setAttribute('mode', 'loading');
    root.appendChild(loader);
  } else {
    // Sin sesión → pantalla de login.
    root.appendChild(document.createElement('app-auth'));
  }

  // Cuando el perfil termina de cargar (redirect OAuth o sesión reabierta).
  bus.on('auth:signed-in', ({ user: u }) => {
    if (document.querySelector('app-navigation')) return;
    document.querySelector('app-auth')?.remove();
    mountShell(u.role);
  });

  // Sesión cerrada o persistida inválida → volver a la pantalla de login.
  bus.on('auth:signed-out', () => {
    document.querySelector('app-navigation')?.remove();
    document.querySelector('app-auth')?.remove();
    root.appendChild(document.createElement('app-auth'));
  });

  // El refresh token de Google murió/no existe → pedir reconexión con acción directa.
  // Persistente (no se va solo) y dedupe para no apilar toasts en cada intento de sync.
  let reauthToastShown = false;
  bus.on('auth:reauth-needed', () => {
    if (reauthToastShown) return;
    reauthToastShown = true;
    bus.emit('notifications:show', {
      message:  'Tu conexión con Google expiró. Reconectá para seguir sincronizando.',
      type:     'warning',
      duration: 0,
      action: {
        label:   'Reconectar',
        onClick: () => {
          reauthToastShown = false;
          getModule<{ signIn: () => Promise<void> }>('auth')?.signIn();
        },
      },
    });
  });
  bus.on('auth:signed-in',  () => { reauthToastShown = false; });
  bus.on('auth:signed-out', () => { reauthToastShown = false; });

  function mountShell(role: string) {
    mount('#app-root', 'app-navigation');
    // Panel admin si corresponde (ruta /admin)
    if (window.location.pathname === '/admin' && role === 'admin') {
      document.body.innerHTML = '';
      document.body.appendChild(document.createElement('app-admin'));
    }
  }
});

boot().catch(err => console.error('[main] Error en boot:', err));
